import { createMemo, createSignal, Match, onCleanup, Show, Switch } from "solid-js";
import { createGameController } from "../../app/controller";
import { joinRoom, loadSeat, storeSeat } from "../../app/api";
import { createOnlineSession, type OnlineSession } from "../../app/online";
import { ThemeToggle } from "../components/ThemeToggle";
import { GameScreen } from "./GameScreen";
import { OnlineLobby } from "./OnlineLobby";
import { DraftScreen } from "./DraftScreen";

export function OnlineApp(props: { roomId: string; onExit: () => void }) {
  const existing = loadSeat(props.roomId);
  const [spectating, setSpectating] = createSignal(false);
  const [session, setSession] = createSignal<OnlineSession | null>(
    existing ? createOnlineSession(props.roomId, existing.token) : null,
  );
  const [joining, setJoining] = createSignal(false);
  const [joinError, setJoinError] = createSignal<string | null>(null);
  const [name, setName] = createSignal("");

  onCleanup(() => session()?.close());

  async function join() {
    setJoining(true);
    setJoinError(null);
    try {
      const result = await joinRoom(props.roomId, name().trim() || undefined);
      storeSeat(props.roomId, result);
      setSession(createOnlineSession(props.roomId, result.token));
    } catch (e) {
      const message = e instanceof Error ? e.message : "Could not join the game.";
      // Both seats taken: fall through to watching the game instead.
      if (/full/i.test(message)) watch();
      else setJoinError(message);
    } finally {
      setJoining(false);
    }
  }

  function watch() {
    setSpectating(true);
    setSession(createOnlineSession(props.roomId, null));
  }

  function leave() {
    session()?.close();
    props.onExit();
  }

  // The fixed-seat controller, created once the server has told us our seat —
  // or a read-only one when we're only watching.
  const controller = createMemo(() => {
    const s = session();
    if (!s) return null;
    const seat = s.mySeat();
    if (seat === null) {
      return spectating() ? createGameController(s, { mySeat: 0, spectator: true }) : null;
    }
    return createGameController(s, { mySeat: seat });
  });

  return (
    <Show
      when={session()}
      fallback={
        <JoinForm
          name={name()}
          setName={setName}
          onJoin={join}
          onWatch={watch}
          busy={joining()}
          error={joinError()}
        />
      }
    >
      {(s) => (
        <Switch fallback={<Centered>Connecting…</Centered>}>
          <Match when={s().status() === "lobby" && spectating()}>
            <Centered>Waiting for the game to start…</Centered>
          </Match>
          <Match when={s().status() === "lobby"}>
            <OnlineLobby roomId={props.roomId} session={s()} onExit={leave} />
          </Match>
          <Match when={s().status() === "drafting"}>
            <Show when={s().draftBackend()} fallback={<Centered>Starting the draft…</Centered>}>
              {(d) => (
                <DraftScreen
                  backend={d()}
                  names={[s().seats()?.[0]?.name ?? "Player 1", s().seats()?.[1]?.name ?? "Player 2"]}
                  onExit={leave}
                  spectator={spectating()}
                />
              )}
            </Show>
          </Match>
          <Match when={s().status() === "playing" || s().status() === "finished"}>
            <Show when={controller()} fallback={<Centered>Syncing…</Centered>}>
              {(c) => (
                <GameScreen
                  controller={c()}
                  onExit={leave}
                  online
                  spectator={spectating()}
                  clock={s().clock() ?? undefined}
                  ratingChange={() => s().rated()}
                />
              )}
            </Show>
          </Match>
          <Match when={s().status() === "disconnected"}>
            <Show when={controller()} fallback={<Centered>Connection lost — reconnecting…</Centered>}>
              {(c) => (
                <>
                  <GameScreen
                    controller={c()}
                    onExit={leave}
                    online
                    spectator={spectating()}
                    clock={s().clock() ?? undefined}
                    ratingChange={() => s().rated()}
                  />
                  <div class="overlay">
                    <div class="overlay-card">
                      <h2>Reconnecting…</h2>
                      <p>The connection dropped. Hang tight — we’ll resync automatically.</p>
                    </div>
                  </div>
                </>
              )}
            </Show>
          </Match>
        </Switch>
      )}
    </Show>
  );
}

function Centered(props: { children: any }) {
  return (
    <div class="app">
      <div class="overlay" style={{ position: "static", background: "transparent", "min-height": "60vh" }}>
        <div class="overlay-card">
          <div class="spinner" />
          <p style={{ margin: "14px 0 0" }}>{props.children}</p>
        </div>
      </div>
    </div>
  );
}

function JoinForm(props: {
  name: string;
  setName: (v: string) => void;
  onJoin: () => void;
  onWatch: () => void;
  busy: boolean;
  error: string | null;
}) {
  return (
    <div class="app">
      <div class="topbar">
        <div class="brand">
          <h1>Chessdeck</h1>
          <span class="tag">join game</span>
        </div>
        <div class="topbar-actions">
          <ThemeToggle />
        </div>
      </div>
      <div class="setup" style={{ "max-width": "440px", margin: "0 auto" }}>
        <div class="panel">
          <h2>You’ve been invited to a game</h2>
          <div class="field">
            <label>Your name</label>
            <input
              type="text"
              placeholder="Player 2"
              value={props.name}
              onInput={(e) => props.setName(e.currentTarget.value)}
              onKeyDown={(e) => e.key === "Enter" && props.onJoin()}
            />
          </div>
          <div class="field-row" style={{ "margin-top": "14px", "justify-content": "flex-end", gap: "8px" }}>
            <button class="btn btn-ghost" onClick={props.onWatch} disabled={props.busy}>
              Watch only
            </button>
            <button class="btn btn-primary" onClick={props.onJoin} disabled={props.busy}>
              {props.busy ? "Joining…" : "Join game"}
            </button>
          </div>
          <Show when={props.error}>
            <div class="error" style={{ "margin-top": "12px" }}>
              {props.error}
            </div>
          </Show>
        </div>
      </div>
    </div>
  );
}
